import { useState, useEffect, useRef } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { BorderBox, Button } from "../components/index.js";
import {
  FileLinesIcon,
  BookIcon,
  BooksIcon,
  MagnifyingGlassIcon,
} from "../assets/icons/index.js";
import { useGlobalContext } from "../context/GlobalContext.jsx";
import MusicWidget from "../components/Music/MusicWidget.jsx";
import CyclesOfLife from "../components/CyclesOfLife.jsx";
import CyclesOfLifeModal from "../components/CyclesOfLifeModal.jsx";
import VowelSoundOfTheDay from "../components/VowelSoundOfTheDay.jsx";
import GrandMasterMessage from "../components/GrandMasterMessage.jsx";
import footerImageLight from "../assets/images/footer_light.png";
import footerImageDark from "../assets/images/footer_dark.png";

export default function Home() {
  const [isCyclesExpanded, setIsCyclesExpanded] = useState(false);
  const [scaleIn, setScaleIn] = useState(false);
  const { user, darkMode } = useGlobalContext();
  const { showMusicBar } = useOutletContext();
  const navigate = useNavigate();

  const lastFocusedElementRef = useRef(null);
  const cyclesModalRef = useRef(null);

  const handleCyclesExpand = () => {
    if (!isCyclesExpanded) {
      lastFocusedElementRef.current = document.activeElement;
    }
    setIsCyclesExpanded(!isCyclesExpanded);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isCyclesExpanded) {
        setIsCyclesExpanded(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCyclesExpanded]);

  useEffect(() => {
    if (isCyclesExpanded) {
      requestAnimationFrame(() => {
        setScaleIn(true);
      });
    } else {
      setScaleIn(false);
    }
  }, [isCyclesExpanded]);

  useEffect(() => {
    if (isCyclesExpanded && cyclesModalRef.current) {
      cyclesModalRef.current.focus();
    } else if (!isCyclesExpanded && lastFocusedElementRef.current) {
      lastFocusedElementRef.current.focus();
    }
  }, [isCyclesExpanded]);

  return (
    <div
      className="flex flex-col gap-2 transition-all duration-300"
      style={{
        paddingBottom: `calc(${
          showMusicBar ? "var(--player-height, 64px)" : "16px"
        } + env(safe-area-inset-bottom))`,
        height: `calc(100dvh - ${showMusicBar ? "10rem" : "4rem"})`,
      }}
    >
      {/* Row 1 - Hidden on mobile and tablet, and when cycles expanded */}
      {!isCyclesExpanded && (
        <div className="hidden lg:flex gap-3 h-1/2 w-full">
          {/* Vowel Sound of the Day */}
          <div className="w-1/2">
            <VowelSoundOfTheDay />
          </div>

          {/* Cycles of Life */}
          <div className="transition-all duration-300 ease-in-out w-1/2">
            <BorderBox
              noPadding
              className="!bg-lz-paper-deep dark:!bg-lz-ink-mid !border-lz-paper-deep-border dark:!border-lz-charcoal-border"
            >
              <div className="flex flex-col w-full h-full">
                <div className="text-lz-candle-dim dark:text-lz-concrete text-2xl font-semibold text-center uppercase shrink-0 my-4">
                  Cycles of Life
                </div>
                <div className="flex-1 min-h-0 mb-4">
                  <CyclesOfLife onExpand={handleCyclesExpand} />
                </div>
              </div>
            </BorderBox>
          </div>
        </div>
      )}

      {/* Expanded Cycles of Life */}
      {isCyclesExpanded && (
        <CyclesOfLifeModal
          scaleIn={scaleIn}
          onClose={handleCyclesExpand}
          modalRef={cyclesModalRef}
        />
      )}

      {/* Row 2 - Hidden when expanded */}
      {!isCyclesExpanded && (
        <div className="gap-3 h-1/2 w-full hidden lg:flex">
          <div className="w-1/3">
            <BorderBox gradient noPadding>
              <div className="flex flex-col w-full h-full">
                <div className="text-lz-candle-dim dark:text-lz-concrete text-2xl font-semibold text-center uppercase shrink-0 my-4">
                  Study
                </div>
                <div className="flex-1 min-h-0 mb-4 flex flex-col justify-center w-3/4 m-auto gap-4 text-lz-ink-mid dark:text-white text-lg">
                  <Link
                    to={`/lessons/${user?.current_lesson_slug}`}
                    className="w-full"
                  >
                    <Button icon={<FileLinesIcon />} className="w-full">
                      Study Your Current Lesson
                    </Button>
                  </Link>

                  <Link
                    to={`/degree/${user?.current_degree_slug || "neophyte"}`}
                  >
                    <Button icon={<BookIcon />} className="w-full">
                      Current Degree
                    </Button>
                  </Link>

                  <Link to="/degrees" className="w-full">
                    <Button icon={<BooksIcon />} className="w-full">
                      Review Lessons by Degree
                    </Button>
                  </Link>

                  <Link to="/explore" className="w-full">
                    <Button icon={<MagnifyingGlassIcon />} className="w-full text-left">
                      Rosicrucian Knowledge Explorer
                    </Button>
                  </Link>
                </div>
              </div>
            </BorderBox>
          </div>
          <div className="w-1/3">
            <GrandMasterMessage />
          </div>
          <div className="w-1/3">
            <BorderBox noPadding>
              <div className="flex flex-col w-full h-full">
                <div className="text-lz-candle-dim dark:text-lz-concrete text-2xl font-semibold text-center uppercase shrink-0 my-4">
                  Music Player
                </div>
                <div className="flex-1 min-h-0 mb-4">
                  <MusicWidget onExpand={() => navigate("/music-player")} />
                </div>
              </div>
            </BorderBox>
          </div>
        </div>
      )}

      {/* Mobile and tablet: show just the first BorderBox of row 2 */}
      <div className="block lg:hidden w-full h-auto">
        <BorderBox gradient className="relative">
          <h1 className="text-2xl font-semibold text-black dark:text-lz-concrete mb-6">
            Welcome to Lezioni!
          </h1>
          <div className="h-full flex flex-col w-full m-auto gap-4 text-lz-ink-mid dark:text-white text-lg">
            <Link
              to={`/lessons/${user?.current_lesson_slug}`}
              className="w-full"
            >
              <Button icon={<FileLinesIcon />} className="w-full">
                Study Your Current Lesson
              </Button>
            </Link>

            <Link to={`/degree/${user?.current_degree_slug || "neophyte"}`}>
              <Button icon={<BookIcon />} className="w-full">
                Current Degree
              </Button>
            </Link>

            <Link to="/degrees" className="w-full">
              <Button icon={<BooksIcon />} className="w-full">
                Review Lessons by Degree
              </Button>
            </Link>

            <Link to="/explore" className="w-full">
              <Button icon={<MagnifyingGlassIcon />} className="w-full">
                Rosicrucian Knowledge Explorer
              </Button>
            </Link>
            {/*
            <Link to="/community" className="w-full">
              <Button icon={<UserGroupIcon />} className="w-full">
                Community
              </Button>
            </Link> */}
          </div>
        </BorderBox>
      </div>
      <img
        src={darkMode ? footerImageDark : footerImageLight}
        alt="Logo for the Grand Lodge of the English Language Jurisdiction of AMORC, Rosicrucian Order"
        className="lg:hidden w-full md:w-1/2 self-center mt-20"
      />
    </div>
  );
}
